import { memo, useCallback, useRef } from 'react';
import classes from 'classnames';
import ErrorMessage from '@/components/ErrorMessage/ErrorMessage';
import styles from './MultiField.module.scss';
import useDebounce from '@/hooks/useDebounce';
import { EventCollectorProps } from '@/hooks/context/EventCollector/types';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';
import TextInputEvent from '@/hooks/context/EventCollector/events/TextInputEvent';

export type Props = {
  value: string[];
  length?: number;
  onChange?: (name: string, value: Array<string | undefined>) => void;
  dataTest?: string;
  name: string;
  error?: string;
  isTouched?: boolean;
  className?: string;
  errorClassName?: string;
};

function MultiField({
  value = [],
  length = 4,
  onChange,
  dataTest = 'multifield',
  name,
  error,
  isTouched,
  eventCollector,
  className,
  errorClassName,
}: Props & EventCollectorProps) {
  const { track } = useEventCollector();
  const fields = useRef<Array<HTMLInputElement | null>>([]);
  const valueRef = useRef(value.join(''));

  const handleEventCollector = useCallback(
    (newValue: string) => {
      if (eventCollector?.dontTrack || valueRef.current === newValue) {
        return;
      }

      track(
        new TextInputEvent({
          value: eventCollector?.dontTrackContent
            ? '<not tracked>'
            : eventCollector?.contentOverride ?? newValue,
          description: eventCollector?.description ?? name ?? '',
        }),
      );

      valueRef.current = newValue;
    },
    [
      eventCollector?.contentOverride,
      eventCollector?.description,
      eventCollector?.dontTrack,
      eventCollector?.dontTrackContent,
      name,
      track,
    ],
  );

  useDebounce(() => handleEventCollector(value.join('')), 1000, [value]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      const index = Number(event.target.name);
      const newMultiFieldValue: Array<string | undefined> = [...value];
      const inputValue = event.target.value.toString();

      // Only select the last written char
      newMultiFieldValue[index] = inputValue[inputValue.length - 1];

      // Navigate to next input
      if (event.target.value) {
        fields.current[index + 1]?.focus();
      } else {
        fields.current[index - 1]?.focus();
      }

      if (onChange) {
        onChange(name, newMultiFieldValue);
      }
    },
    [value, onChange, name],
  );

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const index = Number(event.currentTarget.name);
      const isBackspace = event.keyCode === 8;
      const isRightArrow = event.keyCode === 39;
      const isLeftArrow = event.keyCode === 37;

      // Navigate input fields with keys
      if ((isBackspace && !event.currentTarget.value) || isLeftArrow) {
        fields.current[index - 1]?.focus();
      } else if (isRightArrow) {
        fields.current[index + 1]?.focus();
      }
    },
    [],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const ONLY_NUMBER = /^\d+$/;

      if (!ONLY_NUMBER.test(event.key)) {
        // Allow only digit inputs
        event.preventDefault();
      }
    },
    [],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const ONLY_NUMBER = /^\d+$/;
      const paste: string[] = event.clipboardData
        .getData('text')
        .split('')
        .splice(0, length);

      const hasNonValidChar = paste.some((char) => !ONLY_NUMBER.test(char));

      if (hasNonValidChar) {
        return;
      }

      if (onChange) {
        onChange(name, paste);
      }

      if (paste.length >= length) {
        // All inputs are full, nowhere to navigate anymore
        fields.current[paste.length - 1]?.focus();
      } else {
        // Go to next input if available
        fields.current[paste.length]?.focus();
      }
    },
    [length, name, onChange],
  );

  return (
    <div className={classes(styles.container, className)} data-test={dataTest}>
      {Array(length)
        .fill(null)
        .map((_, index) => (
          <input
            key={index}
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : undefined}
            className={classes(
              styles.input,
              error && isTouched && styles.error,
            )}
            data-test={`input-${index}`}
            name={index.toString()}
            value={value[index] || ''}
            onChange={handleChange}
            onKeyUp={handleKeyUp}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            ref={(node) => {
              fields.current[index] = node;
            }}
          />
        ))}
      {error && isTouched && (
        <div className={classes(styles['error-message'], errorClassName)}>
          <ErrorMessage errors={error} />
        </div>
      )}
    </div>
  );
}

const MultiFieldMemo = memo(MultiField);

export default MultiFieldMemo;
